// 절차 효과음 레시피 표(c019). 장비 줍기(등급별)·대장간 강화 결과·전설 고유 효과·지역3·4 위험·새 보스 패턴 경고.
// 새 오디오 파일·외부 다운로드 없음: 모든 소리는 dsp.ts가 이 표에서 만든다. 소리 설계 메모는 각 줄 주석과 docs/AV-SPEC.md SFX 절.
// 음량 규칙: 렌더 결과는 묶음 목표 창 RMS(100ms 창 최대)에 맞추고 피크 -1.2dBFS 이하(dsp.renderRecipe). 섞임 음량은 gainDb가 정한다.
import { Filt, Noise, Wave, type Env, type FilterSpec, type Freq, type GrainLayer, type Layer, type NoiseLayer, type OscLayer, type Recipe, type Sweep } from './dsp';

/** 소리 묶음: 동시 목소리 상한·간격·덕킹 여부·음량 목표가 묶음마다 다르다 */
export const Cat = { Loot: 0, Forge: 1, Unique: 2, Hazard: 3, Status: 4, Tele: 5, Bow: 6, Grift: 7 } as const;

export interface CatDef {
  readonly name: string;
  /** 렌더 목표 창 RMS(dBFS) */
  readonly targetDb: number;
  /** 묶음 동시 목소리 상한 */
  readonly cap: number;
  /** 같은 묶음 연속 시작 최소 간격(초). 0 = 없음 */
  readonly spacing: number;
  /** 간격 때문에 미룰 수 있는 최대 시간(초). 0 = 미루지 않고 버린다(쏟아지는 줍기는 미뤄서 계단처럼 울린다) */
  readonly maxDelay: number;
  /** 큰 타격 때 덕킹을 받는다(경고·대장간은 받지 않는다 — 반응해야 하는 소리) */
  readonly duck: boolean;
}

export const CATS: readonly CatDef[] = [
  { name: 'loot', targetDb: -16, cap: 3, spacing: 0.055, maxDelay: 0.2, duck: true },
  { name: 'forge', targetDb: -14, cap: 1, spacing: 0, maxDelay: 0, duck: false },
  { name: 'unique', targetDb: -18, cap: 2, spacing: 0.12, maxDelay: 0, duck: true },
  { name: 'hazard', targetDb: -15, cap: 3, spacing: 0.05, maxDelay: 0.1, duck: false }, // 겹친 분출구 경고는 버리지 않고 최대 100ms 미룬다(경고 1.2초 + 0.1 + 피치 흔들림 < 경고 1.4초, 시험)
  { name: 'status', targetDb: -17, cap: 2, spacing: 0.1, maxDelay: 0, duck: true },
  { name: 'tele', targetDb: -14, cap: 2, spacing: 0, maxDelay: 0, duck: false },
  // c022 Lyra 활·화살: 연사·화살비가 쏟아지므로 상한 4·간격 30ms(최대 60ms 미룸). 큰 타격 때 덕킹을 받는다
  { name: 'bow', targetDb: -16, cap: 4, spacing: 0.03, maxDelay: 0.06, duck: true },
  // C12 대균열 장면 소리(입장 굉음·수호자 징·돌파 화음·이정표 반짝·시간 초과): 한 판에 몇 번뿐이고 돌파 화음 + 이정표가 겹치므로 상한 2. 큰 타격 덕킹은 받지 않는다(장면의 마침표)
  { name: 'grift', targetDb: -14, cap: 2, spacing: 0, maxDelay: 0, duck: false },
];

export interface SynthDef {
  readonly name: string;
  readonly cat: number;
  /** 우선순위(높을수록 먼저, 목소리가 모자라면 낮은 것부터 끊는다) */
  readonly prio: number;
  /** 섞임 음량(dB, 효과음 버스 앞) */
  readonly gainDb: number;
  /** 영웅 기준 좌우 팬·거리 감쇠 */
  readonly positional: boolean;
  readonly recipe: Recipe;
}

// ── 짧은 작성 도우미 ──────────────────────────────
const sw = (from: number, to: number, time: number): Sweep => ({ from, to, time });
const env = (a: number, d: number, h = 0, ac = 1): Env => ({ a, d, h, ac });
const lp = (freq: Freq, q = 0.7): FilterSpec => ({ type: Filt.Lp, freq, q });
const hp = (freq: Freq, q = 0.7): FilterSpec => ({ type: Filt.Hp, freq, q });
const bp = (freq: Freq, q = 1): FilterSpec => ({ type: Filt.Bp, freq, q });
type OscOpt = Partial<Pick<OscLayer, 'filter' | 'drive' | 'fm' | 'vib'>>;
const osc = (wave: number, freq: Freq, at: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer => ({ kind: 'osc', wave, freq, at, dur, env: e, gainDb, ...o });
const sine = (freq: Freq, at: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer => osc(Wave.Sine, freq, at, dur, e, gainDb, o);
const noise = (color: number, at: number, dur: number, e: Env, gainDb: number, filter?: FilterSpec, drive?: number): NoiseLayer => ({ kind: 'noise', color, at, dur, env: e, gainDb, filter, drive });
type GrainOpt = Partial<Pick<GrainLayer, 'order' | 'fall' | 'wave' | 'fm' | 'filter'>>;
const grains = (count: number, lo: number, hi: number, at: number, spread: number, dur: number, e: Env, gainDb: number, o: GrainOpt = {}): GrainLayer => ({ kind: 'grains', count, lo, hi, at, spread, dur, env: e, gainDb, ...o });
/** 벨 FM(반송:변조 1:2 따뜻함, 1:3.5 유리, 1:1.414 금속) */
const bell = (ratio: number, index: number, decay: number) => ({ ratio, index, decay });
/** 묵직한 저역 타격: 피치가 떨어지는 사인 + 포화(작은 스피커에서도 배음으로 들린다) */
const thump = (from: number, to: number, at: number, dur: number, d: number, gainDb: number, drive = 2): OscLayer => sine(sw(from, to, Math.min(dur, 0.18)), at, dur, env(0.001, d), gainDb, { drive });
/** 날카로운 시작 딸깍(첫 5ms 안에 소리가 난다) */
const click = (at: number, freq: number, q: number, gainDb: number, d = 0.006): NoiseLayer => noise(Noise.White, at, 0.04, env(0, d), gainDb, bp(freq, q));
const notes = (freqs: readonly number[], at: number, step: number, dur: number, e: Env, gainDb: number, o: OscOpt = {}): OscLayer[] =>
  freqs.map((f, i) => sine(f, at + i * step, dur, e, gainDb - i * 0.5, o));
const R = (dur: number, layers: Layer[], extra: Partial<Recipe> = {}): Recipe => ({ dur, layers, vary: { cents: 25, time: 0.004, db: 0.8 }, ...extra });

// ── 레시피 ─────────────────────────────────────────
const C = Cat;
// 섞임 음량(gainDb)은 qa/c019-sfx 분석에서 기존 파일 효과음의 '창 RMS + gainDb'에 맞춰 잡았다:
// 줍기 일반 -22 ~ 전설 -16(레벨업 -16과 같은 층), 대장간 -13~-16(마을, 홀로 울림), 고유 효과 -24~-28(처치 소리 -26 근처),
// 위험 경고·분출 -17.5·-16.5(용암 분출구와 같은 층), 상태 -23~-28, 보스 경고 -15(경고 착지 teleFire -12보다 한 층 아래, 길게 쌓인다).
const DEFS = {
  // 장비 줍기(Ev.LootPickup b 등급): 등급이 오를수록 음이 늘고 높아지며 꼬리가 길어진다. 일반은 짧은 딸깍 + 뜯음
  pickupCommon: { cat: C.Loot, prio: 1, gainDb: -6, positional: false, recipe: R(0.22, [
    click(0, 3200, 1.2, -6),
    osc(Wave.Tri, sw(880, 820, 0.05), 0, 0.2, env(0.001, 0.05), -8, { filter: lp(3000) }),
    sine(440, 0, 0.15, env(0.002, 0.04), -12),
  ]) },
  // 희귀(파랑): 두 음 상승 벨(E6→B6)
  pickupRare: { cat: C.Loot, prio: 3, gainDb: -5, positional: false, recipe: R(0.36, [
    click(0, 4000, 1.2, -10),
    ...notes([1318.5, 1975.5], 0, 0.06, 0.3, env(0.001, 0.08), -8, { fm: bell(2, 1.2, 0.045) }),
    sine(330, 0, 0.12, env(0.002, 0.04), -14),
  ], { delay: { time: 0.07, fb: 0.25, wet: 0.2, damp: 5000 } }) },
  // 영웅(보라): 작은 저역 + 세 음 아르페지오(C6 E6 G6) + 반짝이 몇 알
  pickupHero: { cat: C.Loot, prio: 4, gainDb: -3.5, positional: false, recipe: R(0.5, [
    thump(160, 90, 0, 0.14, 0.05, -8, 1.5),
    ...notes([1046.5, 1318.5, 1568], 0, 0.05, 0.35, env(0.001, 0.1), -9, { fm: bell(2, 1.4, 0.05) }),
    grains(6, 3000, 6000, 0.08, 0.25, 0.08, env(0.001, 0.03), -20, { order: 1 }),
  ], { delay: { time: 0.09, fb: 0.3, wet: 0.25, damp: 6000 } }) },
  // 세트(초록금): 화음이 한 번에 피어나고 반짝이가 위로 흩어진다
  pickupSet: { cat: C.Loot, prio: 5, gainDb: -2.5, positional: false, recipe: R(0.66, [
    thump(140, 70, 0, 0.18, 0.06, -7, 1.8),
    ...notes([784, 988, 1175, 1568], 0.01, 0.015, 0.55, env(0.002, 0.16), -10, { fm: bell(1, 0.9, 0.08) }),
    grains(10, 2500, 7000, 0.06, 0.35, 0.1, env(0.001, 0.035), -17, { order: 1, fall: 6 }),
  ], { delay: { time: 0.11, fb: 0.35, wet: 0.28, damp: 6500 } }) },
  // 전설(주황금): 깊은 저역 '쿵' + 위로 솟는 쉬익 + 밝은 벨 화음(C6 E6 G6 C7) + 길게 흩어지는 반짝이 꼬리 + 딜레이 공간
  pickupLegend: { cat: C.Loot, prio: 6, gainDb: 0, positional: false, recipe: R(1.15, [
    thump(120, 42, 0, 0.5, 0.16, -3, 2),
    noise(Noise.Brown, 0, 0.25, env(0.002, 0.07), -8, lp(300)),
    noise(Noise.White, 0, 0.03, env(0, 0.004), -10, hp(2000)),
    noise(Noise.Pink, 0.02, 0.5, env(0.25, 0.1, 0, 1.5), -14, bp(sw(1500, 9000, 0.35), 3)),
    ...notes([1046.5, 1318.5, 1568, 2093], 0.12, 0.035, 0.9, env(0.002, 0.25), -10, { fm: bell(2, 2, 0.1) }),
    grains(18, 3500, 9500, 0.15, 0.7, 0.12, env(0.001, 0.04), -16, { order: 1, fall: 12, fm: bell(2.76, 1, 0.03) }),
  ], { delay: { time: 0.13, fb: 0.4, wet: 0.3, damp: 7000 }, drive: 1.2 }) },
  // 강화석: 돌 두 번 부딪는 딸깍 + 작은 반짝
  pickupStone: { cat: C.Loot, prio: 1, gainDb: -7, positional: false, recipe: R(0.22, [
    click(0, 2200, 4, -4, 0.012),
    click(0.045, 1700, 4, -7, 0.012),
    thump(240, 180, 0, 0.08, 0.02, -10, 1),
    sine(2637, 0.03, 0.15, env(0.001, 0.04), -18),
  ]) },
  // 세트·전설 바닥 드롭(Ev.LootDrop b ≥ 3): 빛기둥이 내리꽂히는 쉬익(위→아래) + 저역 + 합창 같은 패드 + 반짝이
  dropRare: { cat: C.Loot, prio: 5, gainDb: -3, positional: true, recipe: R(0.9, [
    noise(Noise.Pink, 0, 0.35, env(0.005, 0.12), -8, bp(sw(6000, 800, 0.3), 2)),
    thump(90, 50, 0, 0.3, 0.1, -6, 1.5),
    ...[523.25, 659.25, 784].map((f) => osc(Wave.Saw, f, 0.02, 0.8, env(0.06, 0.3), -18, { filter: lp(1800), vib: { rate: 5, cents: 8 } })),
    grains(10, 3000, 8000, 0.1, 0.5, 0.1, env(0.001, 0.035), -18, { order: 1, fall: 8 }),
  ], { delay: { time: 0.12, fb: 0.35, wet: 0.25, damp: 6000 } }) },

  // 대장간(Ev.GearEnhance): 성공 = 모루 금속 타격(FM 1:1.414) + 저역 + 불꽃 + 장3도 상승 딩
  forgeSuccess: { cat: C.Forge, prio: 8, gainDb: -1, positional: false, recipe: R(0.95, [
    noise(Noise.White, 0, 0.03, env(0, 0.006), -4, hp(2500)),
    sine(1180, 0, 0.9, env(0.0005, 0.22), -6, { fm: bell(1.414, 4, 0.08) }),
    sine(2650, 0, 0.6, env(0.0005, 0.12), -12, { fm: bell(1.73, 2.5, 0.05) }),
    thump(140, 70, 0, 0.2, 0.06, -6),
    grains(12, 3000, 8000, 0.03, 0.45, 0.06, env(0.0005, 0.015), -18, { fall: 10 }),
    sine(1568, 0.12, 0.6, env(0.002, 0.2), -14, { fm: bell(2, 0.8, 0.08) }),
    sine(1975.5, 0.18, 0.6, env(0.002, 0.2), -15, { fm: bell(2, 0.8, 0.08) }),
  ], { delay: { time: 0.09, fb: 0.3, wet: 0.22, damp: 6000 } }) },
  // 큰 성공(+10·+13·+15): 더 깊은 저역 붐 + 솟는 쉬익 + 팡파레 화음 + 긴 반짝이 꼬리. 단계 2·3은 엔진이 반음씩 올린다
  forgeBig: { cat: C.Forge, prio: 8, gainDb: 1, positional: false, recipe: R(1.4, [
    noise(Noise.White, 0, 0.03, env(0, 0.006), -3, hp(2500)),
    thump(110, 38, 0, 0.6, 0.2, -2, 2.5),
    sine(1050, 0, 1.2, env(0.0005, 0.3), -6, { fm: bell(1.414, 5, 0.1) }),
    noise(Noise.Brown, 0, 0.35, env(0.002, 0.1), -6, lp(250)),
    noise(Noise.Pink, 0.05, 0.6, env(0.35, 0.12, 0, 1.5), -14, bp(sw(2000, 10000, 0.5), 3)),
    ...notes([1046.5, 1318.5, 1568, 2093], 0.18, 0.04, 1.0, env(0.002, 0.35), -11, { fm: bell(2, 1.2, 0.12) }),
    grains(24, 3500, 10000, 0.1, 0.9, 0.12, env(0.001, 0.04), -15, { order: 1, fall: 12, fm: bell(2.76, 1, 0.03) }),
  ], { delay: { time: 0.14, fb: 0.45, wet: 0.3, damp: 7000 }, drive: 1.3 }) },
  // 유지(실패): 막힌 금속 탁 + 단2도로 내려앉는 둔한 두 음 + 재
  forgeFail: { cat: C.Forge, prio: 8, gainDb: -2, positional: false, recipe: R(0.7, [
    click(0, 1800, 1.5, -6),
    sine(620, 0, 0.35, env(0.0005, 0.06), -6, { fm: bell(1.414, 3, 0.03), filter: lp(2000) }),
    thump(120, 80, 0, 0.2, 0.06, -6, 1.5),
    osc(Wave.Tri, 392, 0.12, 0.25, env(0.003, 0.08), -12, { filter: lp(1500) }),
    osc(Wave.Tri, 370, 0.26, 0.4, env(0.003, 0.1), -12, { filter: lp(1200) }),
    noise(Noise.Pink, 0.05, 0.4, env(0.02, 0.12), -20, lp(1200)),
  ], { delay: { time: 0.08, fb: 0.2, wet: 0.12, damp: 3000 } }) },
  // 하락: 갈라지는 딸깍 세 번 + 깨지는 저역 + 아래로 미끄러지는 톱니(필터도 닫힘) + 부스러기 + 우르릉
  forgeDown: { cat: C.Forge, prio: 8, gainDb: -1, positional: false, recipe: R(1.0, [
    noise(Noise.White, 0, 0.04, env(0, 0.008), -3, hp(1500)),
    click(0.05, 2500, 2, -8, 0.008),
    click(0.09, 2000, 2, -10, 0.008),
    thump(160, 55, 0, 0.35, 0.1, -4),
    osc(Wave.Saw, sw(440, 110, 0.6), 0.08, 0.8, env(0.01, 0.25), -12, { filter: lp(sw(2500, 400, 0.6), 1) }),
    grains(14, 300, 1200, 0.1, 0.6, 0.05, env(0.0005, 0.012), -14, { order: -1, fall: 8, wave: Wave.Tri }),
    noise(Noise.Brown, 0, 0.6, env(0.01, 0.2), -12, lp(200)),
  ], { delay: { time: 0.1, fb: 0.25, wet: 0.15, damp: 2500 } }) },
  // 보호(주문서): 균열이 시작되다 청색 결계 유리 울림(FM 1:3.5)에 막힌다 + 위로 오르는 사인 + 청색 알갱이
  forgeProtected: { cat: C.Forge, prio: 8, gainDb: -1.5, positional: false, recipe: R(1.0, [
    noise(Noise.White, 0, 0.04, env(0, 0.008), -4, hp(1500)),
    click(0.05, 2500, 2, -9, 0.008),
    thump(130, 75, 0.08, 0.18, 0.05, -8, 1.5),
    sine(1760, 0.1, 0.8, env(0.001, 0.25), -8, { fm: bell(3.5, 2, 0.06) }),
    sine(2637, 0.14, 0.7, env(0.001, 0.22), -12, { fm: bell(3.5, 1.5, 0.05) }),
    sine(sw(440, 880, 0.3), 0.08, 0.5, env(0.05, 0.15), -14),
    grains(10, 2500, 6000, 0.15, 0.4, 0.1, env(0.001, 0.03), -18, { order: 1, fm: bell(2, 0.8, 0.03) }),
  ], { delay: { time: 0.12, fb: 0.35, wet: 0.25, damp: 6000 } }) },

  // 전설 고유 효과(Ev.GearUnique, gearCues 종류별): 짧고 밝은 표지. 처치마다 날 수 있어 음량은 낮다
  uniqShield: { cat: C.Unique, prio: 3, gainDb: -6, positional: true, recipe: R(0.5, [
    sine(2349, 0, 0.45, env(0.001, 0.12), -10, { fm: bell(1.414, 2, 0.04) }),
    noise(Noise.Pink, 0, 0.3, env(0.03, 0.08), -8, bp(sw(900, 3500, 0.2), 2)),
    sine(1175, 0.02, 0.4, env(0.002, 0.1), -14, { vib: { rate: 9, cents: 15 } }),
  ], { delay: { time: 0.07, fb: 0.3, wet: 0.2, damp: 5000 } }) },
  uniqHeal: { cat: C.Unique, prio: 3, gainDb: -10, positional: true, recipe: R(0.5, [
    ...notes([784, 1175], 0, 0.07, 0.4, env(0.004, 0.12), -9, { fm: bell(1, 0.6, 0.1) }),
    noise(Noise.Pink, 0, 0.35, env(0.05, 0.1), -22, hp(4000)),
    grains(5, 2000, 4500, 0.05, 0.2, 0.08, env(0.001, 0.03), -20, { order: 1 }),
  ]) },
  uniqMp: { cat: C.Unique, prio: 3, gainDb: -10, positional: true, recipe: R(0.45, [
    sine(sw(300, 900, 0.08), 0, 0.15, env(0.002, 0.04), -8),
    sine(1975.5, 0.05, 0.35, env(0.001, 0.1), -11, { fm: bell(3.5, 1.2, 0.04) }),
    osc(Wave.Tri, sw(600, 1200, 0.05), 0.1, 0.08, env(0.001, 0.02), -16),
  ]) },
  uniqAwaken: { cat: C.Unique, prio: 3, gainDb: -10, positional: true, recipe: R(0.5, [
    sine(sw(70, 95, 0.2), 0, 0.35, env(0.03, 0.12), -8, { drive: 2 }),
    grains(14, 1500, 5000, 0, 0.35, 0.02, env(0, 0.004), -12, { wave: Wave.Tri }),
    noise(Noise.Pink, 0, 0.3, env(0.05, 0.08), -14, bp(sw(600, 2000, 0.25), 1.5)),
  ]) },
  uniqRefund: { cat: C.Unique, prio: 3, gainDb: -8, positional: false, recipe: R(0.35, [
    click(0, 4500, 6, -6, 0.005),
    click(0.09, 3200, 6, -8, 0.005),
    sine(2093, 0.02, 0.3, env(0.001, 0.08), -14, { fm: bell(2, 1, 0.03) }),
  ]) },
  uniqLowHp: { cat: C.Unique, prio: 2, gainDb: -9, positional: false, recipe: R(0.55, [
    thump(70, 50, 0, 0.18, 0.05, -4),
    thump(65, 48, 0.2, 0.2, 0.06, -6),
    osc(Wave.Saw, 110, 0, 0.5, env(0.05, 0.15), -18, { filter: lp(300) }),
  ]) },

  // 지역3 서리 분출구 경고(HazardWarn, 경고 1.4초): 첫 5ms 유리 딱 + 점점 잦아지는 얼음 갈라짐 + 조여드는 삐걱임. 1.2초에 끝나 분출 전에 비운다
  iceSpikeWarn: { cat: C.Hazard, prio: 7, gainDb: -2.5, positional: true, recipe: R(1.2, [
    sine(3520, 0, 0.15, env(0.0005, 0.03), -8, { fm: bell(3.5, 2, 0.02) }),
    grains(24, 2500, 7000, 0.02, 1.0, 0.04, env(0.0005, 0.008), -12, { order: 1, fall: -8 }),
    osc(Wave.Saw, sw(180, 260, 1.1), 0, 1.15, env(0.9, 0.08, 0, 2), -12, { filter: bp(1200, 6), vib: { rate: 14, cents: 30 } }),
    noise(Noise.Pink, 0, 1.15, env(1.0, 0.05, 0, 2), -16, hp(sw(3000, 8000, 1.1))),
  ]) },
  // 서리 분출(HazardErupt): 저역 쿵 + 터지는 잡음 + 유리 파편 반짝이 + 높은 울림
  iceSpike: { cat: C.Hazard, prio: 7, gainDb: -1.5, positional: true, recipe: R(0.8, [
    thump(130, 50, 0, 0.35, 0.1, -4),
    noise(Noise.White, 0, 0.2, env(0, 0.04), -5, hp(1800)),
    grains(26, 2500, 9000, 0.005, 0.35, 0.1, env(0.0005, 0.025), -10, { fall: 10, fm: bell(2.76, 1.5, 0.02) }),
    sine(3136, 0, 0.5, env(0.001, 0.12), -14, { fm: bell(3.5, 2.5, 0.04) }),
  ], { delay: { time: 0.06, fb: 0.3, wet: 0.2, damp: 6000 } }) },
  // 서리 균열 동상(HeroStatus a3 c1) · 둔화 시작(a1): 얼음 갈라지는 딱 세 번 + 낮은 삐걱 + 작은 얼음 소리
  frostCrack: { cat: C.Status, prio: 2, gainDb: -7, positional: false, recipe: R(0.35, [
    click(0, 3000, 3, -3, 0.005),
    click(0.04, 2200, 3, -6, 0.005),
    click(0.1, 3800, 3, -9, 0.005),
    sine(sw(220, 160, 0.2), 0, 0.3, env(0.002, 0.08), -12, { vib: { rate: 25, cents: 40 } }),
    sine(4186, 0.02, 0.2, env(0.0005, 0.04), -18, { fm: bell(3.5, 1, 0.02) }),
  ]) },
  // 지역4 독 분출구 경고(HazardWarn): 첫 방울 + 점점 빨라지는 거품 + 차오르는 쉿 + 낮은 웅웅
  gasWarn: { cat: C.Hazard, prio: 7, gainDb: -2.5, positional: true, recipe: R(1.2, [
    sine(sw(180, 420, 0.05), 0, 0.12, env(0.001, 0.03), -6),
    ...[0.12, 0.26, 0.38, 0.5, 0.6, 0.7, 0.78, 0.86, 0.93, 1.0].map((at, i) => sine(sw(150 + i * 18, (150 + i * 18) * 2.3, 0.05), at, 0.08, env(0.001, 0.025), -16 + i * 0.8)),
    noise(Noise.Pink, 0, 1.15, env(1.0, 0.05, 0, 2.2), -12, bp(sw(1500, 4000, 1.1), 1.5)),
    osc(Wave.Saw, 55, 0, 1.15, env(0.6, 0.2), -16, { filter: lp(220) }),
  ]) },
  // 독 분출(HazardErupt): 펑 저역 + 쏟아지는 쉬익(대역이 내려앉음) + 젖은 거품 + 지글거림
  gasBurst: { cat: C.Hazard, prio: 7, gainDb: -1.5, positional: true, recipe: R(0.85, [
    thump(110, 45, 0, 0.3, 0.09, -4),
    noise(Noise.White, 0, 0.7, env(0.002, 0.18), -6, bp(sw(2500, 900, 0.5), 0.9)),
    grains(12, 300, 900, 0.03, 0.4, 0.06, env(0.001, 0.015), -14),
    noise(Noise.Pink, 0, 0.6, env(0.01, 0.15), -18, hp(5000)),
  ]) },
  // 독 늪·중독 시작(HeroStatus a2): 걸쭉한 거품 셋 + 젖은 저역
  poisonPool: { cat: C.Status, prio: 2, gainDb: -6, positional: false, recipe: R(0.45, [
    sine(sw(140, 380, 0.06), 0, 0.12, env(0.001, 0.035), -6),
    sine(sw(200, 520, 0.06), 0.12, 0.12, env(0.001, 0.035), -9),
    sine(sw(170, 450, 0.06), 0.22, 0.12, env(0.001, 0.035), -12),
    noise(Noise.Brown, 0, 0.35, env(0.01, 0.1), -12, lp(400)),
  ]) },
  // 중독 피해 틱(HeroStatus a3 c2, 0.5초마다): 아주 작은 지글 방울
  poisonTick: { cat: C.Status, prio: 1, gainDb: -11, positional: false, recipe: R(0.22, [
    sine(sw(260, 600, 0.04), 0, 0.08, env(0.001, 0.02), -8),
    noise(Noise.Pink, 0, 0.18, env(0.005, 0.05), -14, bp(3000, 2)),
  ]) },

  // 보스 새 패턴 경고(Ev.BossPattern a 4~8, 경고가 나타나는 틱). 첫 소리는 5ms 안, 길이는 각 패턴 경고 시간보다 짧다(tele 시험)
  // 돌진(경고 1.0초): 콧김 + 차오르는 으르렁(톱니 + 비브라토 + 포화) + 땅 긁기 두 번 + 솟는 바람
  teleCharge: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(0.92, [
    noise(Noise.Pink, 0, 0.18, env(0.002, 0.05), -4, bp(700, 1.5)),
    osc(Wave.Saw, sw(60, 95, 0.85), 0, 0.9, env(0.5, 0.12, 0, 1.5), -6, { filter: lp(500, 2), vib: { rate: 18, cents: 60 }, drive: 2 }),
    noise(Noise.White, 0.25, 0.25, env(0.005, 0.06), -12, bp(sw(1800, 900, 0.2), 2)),
    noise(Noise.White, 0.5, 0.25, env(0.005, 0.06), -10, bp(sw(1800, 900, 0.2), 2)),
    noise(Noise.Pink, 0, 0.9, env(0.8, 0.06, 0, 2), -12, bp(sw(400, 2500, 0.85), 2)),
    sine(sw(40, 55, 0.8), 0, 0.9, env(0.7, 0.1, 0, 1.5), -8),
  ]) },
  // 지진(경고 1.4초): 첫 저역 쿵 + 차오르는 땅울림(갈색 잡음·초저역) + 돌 갈리는 대역 잡음 + 자갈
  teleQuake: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.25, [
    thump(90, 45, 0, 0.4, 0.12, -3),
    noise(Noise.Brown, 0, 1.2, env(0.9, 0.1, 0, 1.6), -4, lp(180)),
    sine(sw(32, 40, 1.1), 0, 1.2, env(0.9, 0.1, 0, 1.4), -6, { drive: 1.5 }),
    noise(Noise.Pink, 0, 1.2, env(0.8, 0.1, 0, 1.5), -12, bp(sw(400, 250, 1.0), 3)),
    grains(16, 800, 2500, 0.1, 1.0, 0.03, env(0, 0.006), -16, { wave: Wave.Tri, fall: -6 }),
  ]) },
  // 세 갈래 물기(경고 1.1초): 으르렁 + 쉿 뒤에 턱 딱 세 번(점점 높게) — 세 번 문다는 박자를 미리 들려준다
  teleBite: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.0, [
    osc(Wave.Saw, 140, 0, 0.45, env(0.005, 0.15), -8, { filter: lp(1200, 3), vib: { rate: 28, cents: 120 }, drive: 2 }),
    noise(Noise.White, 0, 0.3, env(0.003, 0.1), -14, bp(3000, 1)),
    ...[0.45, 0.6, 0.75].flatMap((at, i): Layer[] => [
      click(at, 1600, 2, -4 + i, 0.008),
      sine(sw(800 + i * 100, 550 + i * 70, 0.02), at, 0.05, env(0, 0.01), -10),
    ]),
  ]) },
  // 얼음 십자(경고 1.3초): 유리 딱 + 줄마다 하나씩 오르는 얼음 음 넷 + 차가운 바람 + 낮은 서리 웅웅
  teleCross: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.2, [
    sine(2349, 0, 0.5, env(0.0005, 0.15), -6, { fm: bell(3.5, 3, 0.05) }),
    ...[1175, 1397, 1760, 2349].map((f, i) => sine(f, 0.1 + i * 0.15, 0.4, env(0.002, 0.12), -10, { fm: bell(3.5, 1.5, 0.06) })),
    noise(Noise.Pink, 0, 1.15, env(0.9, 0.06, 0, 2), -12, bp(sw(1000, 6000, 1.0), 2)),
    sine(98, 0, 1.1, env(0.3, 0.4), -14, { vib: { rate: 6, cents: 20 } }),
  ]) },
  // 포자(경고 1.2초): 포자 터지는 '푹' 다섯 번(원 다섯 개) + 젖은 꾸르륵 + 낮은 유기물 웅웅
  teleSpores: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.1, [
    ...[0, 0.18, 0.34, 0.5, 0.66].map((at, i) => noise(Noise.Pink, at, 0.12, env(0.002, 0.035), -4 - (i % 2) * 2, bp(700 + i * 100, 1.2))),
    sine(sw(200, 90, 0.05), 0.02, 0.1, env(0.001, 0.03), -12),
    sine(sw(220, 95, 0.05), 0.36, 0.1, env(0.001, 0.03), -12),
    osc(Wave.Tri, 73, 0, 1.05, env(0.4, 0.3), -14, { filter: lp(400), vib: { rate: 4, cents: 30 } }),
    noise(Noise.White, 0, 1.0, env(0.6, 0.1, 0, 1.5), -20, bp(5000, 0.8)),
  ]) },

  // ── C2 지역5 사막(sand)·지역6 심연(void) 위험 + 보스 새 패턴 4종 ─────────────
  // 모래 분출구 경고(HazardWarn, 경고 1.4초): 첫 알갱이 틱 + 쏟아지는 모래(낙하 알갱이가 점점 촘촘) + 좁은 바람 휘파람이 차오름 + 낮은 땅울림. 1.2초에 끝난다
  sandWarn: { cat: C.Hazard, prio: 7, gainDb: -2.5, positional: true, recipe: R(1.2, [
    click(0, 2600, 1.5, -8, 0.008),
    grains(40, 900, 3200, 0.02, 1.1, 0.03, env(0, 0.008), -14, { order: 1, fall: -4, wave: Wave.Tri }),
    noise(Noise.Pink, 0, 1.15, env(0.9, 0.06, 0, 2), -14, bp(sw(1100, 2300, 1.1), 7)),
    noise(Noise.Brown, 0, 1.15, env(0.5, 0.2), -10, lp(160)),
  ]) },
  // 모래 간헐천 분출(HazardErupt): 둔한 쿵 + 터지는 모래 쉬익(대역이 내려앉음) + 흩날려 떨어지는 알갱이 꼬리
  sandBurst: { cat: C.Hazard, prio: 7, gainDb: -1.5, positional: true, recipe: R(0.85, [
    thump(100, 48, 0, 0.3, 0.09, -4, 1.6),
    noise(Noise.White, 0, 0.55, env(0.004, 0.16), -6, bp(sw(4200, 1300, 0.45), 0.9)),
    noise(Noise.Brown, 0, 0.5, env(0.005, 0.15), -10, lp(300)),
    grains(22, 700, 3000, 0.1, 0.6, 0.04, env(0, 0.01), -13, { order: -1, fall: 5, wave: Wave.Tri }),
  ]) },
  // 유사 발 묶임 시작(HeroStatus a4): 발이 푹 빠지는 서걱(알갱이 아래로 훑음) + 낮은 눌림 + 무거운 갈색 잡음
  sandSlow: { cat: C.Status, prio: 2, gainDb: -6, positional: false, recipe: R(0.45, [
    click(0, 1800, 1.2, -8, 0.01),
    thump(110, 55, 0, 0.25, 0.08, -8, 1.3),
    noise(Noise.Pink, 0, 0.4, env(0.02, 0.14), -10, bp(sw(2600, 600, 0.35), 1.2)),
    grains(12, 300, 1500, 0.03, 0.25, 0.04, env(0, 0.01), -14, { order: -1, fall: 4, wave: Wave.Tri }),
  ]) },
  // 유사 피해 틱(HeroStatus a3 c1, sand 테마): 아주 작은 모래 긁힘
  sandGrit: { cat: C.Status, prio: 1, gainDb: -11, positional: false, recipe: R(0.2, [
    noise(Noise.White, 0, 0.14, env(0.001, 0.04), -8, bp(sw(3200, 1500, 0.1), 1.2)),
    grains(5, 800, 2400, 0, 0.08, 0.03, env(0, 0.008), -12, { wave: Wave.Tri }),
  ]) },
  // 심연 분출구 경고(HazardWarn): 첫 유리 울림 + 빨려드는 쉬익(역재생처럼 서서히 차오르며 대역이 내려옴) + 낮은 웅웅 + 두 음 공명이 천천히 커짐
  voidWarn: { cat: C.Hazard, prio: 7, gainDb: -2.5, positional: true, recipe: R(1.2, [
    sine(1480, 0, 0.3, env(0.001, 0.08), -12, { fm: bell(3.5, 1.5, 0.05) }),
    noise(Noise.Pink, 0, 1.15, env(1.0, 0.05, 0, 2.4), -10, bp(sw(4200, 380, 1.1), 1.4)),
    osc(Wave.Saw, 52, 0, 1.15, env(0.7, 0.2), -14, { filter: lp(200), vib: { rate: 5, cents: 25 } }),
    sine(740, 0.05, 1.1, env(0.95, 0.06, 0, 2), -18, { fm: bell(2.01, 1.2, 0.2), vib: { rate: 6, cents: 12 } }),
    sine(1109, 0.05, 1.1, env(0.95, 0.06, 0, 2), -20, { fm: bell(2.01, 1.2, 0.2), vib: { rate: 6, cents: 12 } }),
  ]) },
  // 공허 기둥 분출(HazardErupt): 깊은 쿵 + 터진 뒤 안으로 빨리는 쉬익 + 유리 공명 둘 + 낮게 남는 울림
  voidBurst: { cat: C.Hazard, prio: 7, gainDb: -1.5, positional: true, recipe: R(0.9, [
    thump(85, 34, 0, 0.45, 0.14, -3, 1.8),
    noise(Noise.White, 0, 0.3, env(0.002, 0.07), -9, hp(1500)),
    noise(Noise.Pink, 0.04, 0.6, env(0.02, 0.2), -10, bp(sw(3000, 350, 0.55), 1.3)),
    sine(1397, 0, 0.7, env(0.001, 0.2), -14, { fm: bell(3.5, 2, 0.06) }),
    sine(932, 0.02, 0.7, env(0.001, 0.22), -16, { fm: bell(3.5, 1.6, 0.06) }),
  ], { delay: { time: 0.08, fb: 0.3, wet: 0.2, damp: 4500 } }) },
  // 심연 침식 시작(HeroStatus a5): 내려앉는 두 음(불협 3온음) + 갉아먹는 지글 + 낮은 눌림
  voidCorrode: { cat: C.Status, prio: 2, gainDb: -6, positional: false, recipe: R(0.5, [
    sine(sw(520, 330, 0.3), 0, 0.4, env(0.002, 0.12), -8, { fm: bell(1.414, 1.5, 0.08) }),
    sine(sw(735, 465, 0.3), 0.02, 0.4, env(0.002, 0.12), -11, { fm: bell(1.414, 1.5, 0.08) }),
    noise(Noise.Pink, 0, 0.35, env(0.01, 0.1), -16, hp(3500)),
    thump(90, 50, 0, 0.2, 0.06, -9, 1.3),
  ]) },
  // 침식 피해 틱(HeroStatus a3 c2, void 테마): 작은 유리 핑 + 낮은 쿵(독 틱의 지글과 다르게 맑다)
  voidTick: { cat: C.Status, prio: 1, gainDb: -11, positional: false, recipe: R(0.22, [
    sine(1760, 0, 0.18, env(0.001, 0.04), -9, { fm: bell(3.5, 1.2, 0.03) }),
    sine(sw(130, 80, 0.06), 0, 0.12, env(0.001, 0.03), -8),
    noise(Noise.Pink, 0, 0.1, env(0.003, 0.025), -20, hp(4000)),
  ]) },

  // 창날 세 줄(lance3, 경고 1.0초): 첫 쇠 긁힘 + 줄마다 하나씩 오르는 쇳소리 셋(좌→중→우) + 가늘게 조여드는 바람
  teleLance: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(0.92, [
    click(0, 3400, 2, -3, 0.006),
    sine(sw(900, 1400, 0.1), 0, 0.15, env(0.001, 0.04), -10, { fm: bell(1.414, 2, 0.03) }),
    ...[0.3, 0.5, 0.7].flatMap((at, i): Layer[] => [
      click(at, 2800 + i * 500, 3, -4, 0.007),
      sine(1175 * [1, 1.26, 1.5][i], at, 0.2, env(0.001, 0.07), -9, { fm: bell(1.414, 2.5, 0.04) }),
    ]),
    noise(Noise.Pink, 0, 0.88, env(0.8, 0.05, 0, 2), -16, bp(sw(1500, 5000, 0.85), 3)),
    sine(110, 0, 0.85, env(0.5, 0.2), -14),
  ]) },
  // 거대 도끼 처형(execute, 경고 1.6초): 첫 육중한 쿵 + 쇠사슬 끌림(대역 잡음 + 고리 딸깍) + 차오르는 저역 압박 + 천천히 올라가는 쇠 울림
  teleExecute: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.4, [
    thump(75, 38, 0, 0.45, 0.14, -3, 2),
    noise(Noise.Brown, 0, 1.35, env(1.0, 0.12, 0, 1.7), -5, lp(200)),
    sine(sw(36, 52, 1.3), 0, 1.35, env(1.0, 0.1, 0, 1.4), -7, { drive: 1.5 }),
    noise(Noise.Pink, 0.1, 1.2, env(0.7, 0.1, 0, 1.6), -13, bp(sw(500, 1100, 1.1), 4)),
    ...[0.2, 0.45, 0.7, 0.9, 1.05, 1.18].map((at, i) => click(at, 1500 + i * 120, 4, -9 + i * 0.8, 0.012)),
    sine(sw(185, 330, 1.2), 0.1, 1.25, env(0.9, 0.1, 0, 2), -17, { fm: bell(1.414, 2, 0.1) }),
  ]) },
  // 둘레 소용돌이(vortex, 경고 1.4초): 첫 알갱이 틱 + 도는 바람(대역이 오르내리며 떨림) + 여섯 소용돌이를 알리는 알갱이 + 낮은 회전 웅웅. void 맵에서는 결정이 피치를 낮춘다
  teleVortex: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.1, [
    click(0, 2200, 1.5, -5, 0.008),
    noise(Noise.Pink, 0, 1.05, env(0.7, 0.1, 0, 1.6), -7, bp(sw(500, 2200, 1.0), 3)),
    osc(Wave.Sine, sw(130, 200, 1.0), 0, 1.05, env(0.6, 0.15), -9, { vib: { rate: 6, cents: 70 } }),
    grains(36, 600, 3200, 0.05, 0.95, 0.04, env(0, 0.01), -14, { order: 1, wave: Wave.Tri }),
    noise(Noise.Brown, 0, 1.05, env(0.5, 0.2), -12, lp(220)),
  ]) },
  // 여섯 갈래 별빛(star, 경고 1.4초): 첫 유리 딱 + 여섯 줄이 켜지듯 차례로 오르는 맑은 음 여섯 + 반짝이 + 바람 한 줄기 + 낮은 받침
  teleStar: { cat: C.Tele, prio: 9, gainDb: -1, positional: true, recipe: R(1.25, [
    sine(2637, 0, 0.4, env(0.0005, 0.1), -7, { fm: bell(3.5, 2.5, 0.05) }),
    ...[1319, 1480, 1661, 1865, 2093, 2349].map((f, i) => sine(f, 0.08 + i * 0.13, 0.35, env(0.002, 0.1), -10 - i * 0.3, { fm: bell(2, 1.2, 0.06) })),
    grains(18, 3500, 9000, 0.05, 1.0, 0.08, env(0.001, 0.03), -18, { order: 1, fall: 6 }),
    noise(Noise.Pink, 0, 1.2, env(0.9, 0.06, 0, 2), -16, bp(sw(1800, 7000, 1.1), 2.5)),
    sine(87, 0, 1.2, env(0.3, 0.4), -14, { vib: { rate: 5, cents: 18 } }),
  ]) },

  // ── c022 Lyra(궁수) 활·화살(효과음 큐 이름 계약: lyraSfx.ts LYRA_SFX) ─────────────
  // 시위 당김(bow_draw): 나무 활대가 휘는 낮은 삐걱(톱니 + 좁은 대역 + 떨림) + 차오르는 시위 긴장 쉿 + 첫 딸깍
  bowDraw: { cat: C.Bow, prio: 2, gainDb: -10, positional: false, recipe: R(0.42, [
    click(0, 2600, 3, -10, 0.006),
    osc(Wave.Saw, sw(95, 120, 0.35), 0.01, 0.38, env(0.12, 0.08), -10, { filter: bp(sw(700, 1100, 0.35), 5), vib: { rate: 22, cents: 45 } }),
    noise(Noise.Pink, 0.02, 0.36, env(0.25, 0.06, 0, 1.6), -14, bp(sw(900, 2600, 0.33), 3)),
  ]) },
  // 발사(bow_release, 1·2타): 시위 튕김(삼각파가 빠르게 내려앉음) + 날카로운 딸깍 + 바람 가르는 쉭
  bowRelease: { cat: C.Bow, prio: 4, gainDb: -7, positional: false, recipe: R(0.24, [
    click(0, 3200, 2, -4, 0.005),
    osc(Wave.Tri, sw(260, 190, 0.06), 0, 0.2, env(0.001, 0.045), -6, { filter: lp(2400) }),
    noise(Noise.White, 0.005, 0.18, env(0.004, 0.05), -10, bp(sw(4200, 1600, 0.15), 1.4)),
  ]) },
  // 강사(bow_release_heavy, 3타 관통): 더 깊은 시위 '퉁' + 저역 반동 + 길게 감기는 바람 소용돌이(대역이 내려앉으며 떨림)
  bowReleaseHeavy: { cat: C.Bow, prio: 5, gainDb: -5, positional: false, recipe: R(0.5, [
    click(0, 2400, 2, -3, 0.006),
    osc(Wave.Tri, sw(180, 120, 0.08), 0, 0.3, env(0.001, 0.07), -5, { filter: lp(2000), drive: 1.5 }),
    thump(120, 60, 0, 0.16, 0.05, -8, 1.5),
    noise(Noise.Pink, 0.01, 0.45, env(0.01, 0.13), -8, bp(sw(3800, 900, 0.4), 2)),
    noise(Noise.White, 0.02, 0.4, env(0.02, 0.1), -16, hp(5000)),
  ], { delay: { time: 0.06, fb: 0.2, wet: 0.12, damp: 4000 } }) },
  // 명중(arrow_hit): 몸에 박히는 둔한 '턱'(짧은 저역) + 딸깍 + 짧은 잡음
  arrowHit: { cat: C.Bow, prio: 3, gainDb: -9, positional: true, recipe: R(0.2, [
    thump(320, 140, 0, 0.12, 0.03, -4, 1.8),
    click(0, 1800, 2, -6, 0.006),
    noise(Noise.Pink, 0, 0.1, env(0.001, 0.025), -12, lp(2500)),
  ]) },
  // 관통(arrow_pierce): 꿰뚫고 지나가는 날카로운 쉭(고역이 빠르게 내려감) + 금속성 짧은 울림 + 딸깍
  arrowPierce: { cat: C.Bow, prio: 4, gainDb: -9, positional: true, recipe: R(0.28, [
    click(0, 3600, 3, -6, 0.004),
    noise(Noise.White, 0, 0.2, env(0.002, 0.05), -6, bp(sw(6000, 2200, 0.16), 2)),
    sine(2480, 0.01, 0.22, env(0.001, 0.05), -14, { fm: bell(1.414, 1.5, 0.03) }),
  ]) },
  // 화살비(arrow_rain, 낙하 한 파): 위에서 떨어지며 낮아지는 휘파람 알갱이 + 내려앉는 바람 쉬익 + 바닥에 박히는 톡톡
  arrowRain: { cat: C.Bow, prio: 4, gainDb: -8, positional: true, recipe: R(0.62, [
    grains(10, 2400, 4200, 0, 0.3, 0.12, env(0.01, 0.05), -12, { order: -1, fall: -4 }),
    noise(Noise.Pink, 0, 0.42, env(0.05, 0.12), -10, bp(sw(4200, 1300, 0.38), 1.5)),
    grains(8, 900, 1800, 0.22, 0.32, 0.03, env(0, 0.008), -10, { wave: Wave.Tri }),
  ]) },
  // 덫 설치(trap_set): 금속 걸쇠 딸깍-딸깍 + 짧은 쇠 울림 + 룬이 켜지는 작은 반짝
  trapSet: { cat: C.Bow, prio: 3, gainDb: -9, positional: true, recipe: R(0.4, [
    click(0, 3000, 4, -4, 0.006),
    click(0.08, 2200, 4, -6, 0.008),
    sine(1500, 0.08, 0.25, env(0.001, 0.06), -10, { fm: bell(1.414, 2.5, 0.04) }),
    grains(5, 3000, 6000, 0.12, 0.2, 0.06, env(0.001, 0.02), -18, { order: 1 }),
  ]) },
  // 덫 발동(trap_snap): 탁 닫히는 강한 딸깍 + 그물이 조여드는 밧줄 삐걱(톱니 떨림) + 저역 + 짧은 잡음
  trapSnap: { cat: C.Bow, prio: 5, gainDb: -6, positional: true, recipe: R(0.42, [
    click(0, 2000, 1.5, -2, 0.008),
    thump(180, 80, 0, 0.14, 0.04, -6, 1.6),
    noise(Noise.White, 0, 0.08, env(0.001, 0.02), -8, hp(1800)),
    osc(Wave.Saw, sw(160, 110, 0.3), 0.03, 0.34, env(0.02, 0.1), -12, { filter: bp(900, 4), vib: { rate: 30, cents: 70 } }),
  ]) },
  // 폭발 화살(arrow_explode): 깊은 '쿵' + 터지는 잡음 + 사방으로 흩어지는 바람 쉬익 + 파편 알갱이
  arrowExplode: { cat: C.Bow, prio: 6, gainDb: -3, positional: true, recipe: R(0.85, [
    thump(110, 40, 0, 0.45, 0.13, -2, 2.2),
    noise(Noise.White, 0, 0.25, env(0.001, 0.06), -5, hp(1200)),
    noise(Noise.Pink, 0.02, 0.7, env(0.01, 0.2), -9, bp(sw(2500, 600, 0.6), 1)),
    grains(14, 1500, 5000, 0.01, 0.4, 0.05, env(0.0005, 0.012), -14, { fall: 8, wave: Wave.Tri }),
  ], { delay: { time: 0.08, fb: 0.25, wet: 0.15, damp: 3500 } }) },
  // 각성(lyra_awaken, 폭풍 화살비): 솟구치는 폭풍 바람(대역이 위로 열림) + 저역 붐 + 바람 화음 벨(E5 B5 E6 B6) + 반짝이 꼬리
  lyraAwaken: { cat: C.Bow, prio: 8, gainDb: -2, positional: false, recipe: R(1.3, [
    thump(100, 45, 0, 0.5, 0.15, -4, 2),
    noise(Noise.Pink, 0, 1.2, env(0.5, 0.3, 0, 1.4), -6, bp(sw(400, 6000, 0.9), 1.6)),
    ...notes([659.25, 987.75, 1318.5, 1975.5], 0.15, 0.05, 1.0, env(0.003, 0.3), -12, { fm: bell(2, 1.2, 0.1) }),
    grains(20, 3000, 9000, 0.25, 0.9, 0.1, env(0.001, 0.035), -17, { order: 1, fall: 10 }),
  ], { delay: { time: 0.12, fb: 0.35, wet: 0.25, damp: 6500 }, drive: 1.2 }) },

  // ── C12 대균열(FieldEv.GriftEnter·RiftGuardian·GriftResult, griftSfx.ts) ──
  // 입장(griftEnter): 바닥이 꺼지는 낮은 굉음(70→36Hz 포화 사인 + 갈색 잡음 부풂) + 감겨 드는 소용돌이 바람(대역 2.4k→350Hz) + 낮은 톱니 으르렁(떨림) + 끝의 둔탁한 닫힘
  griftEnter: { cat: C.Grift, prio: 7, gainDb: -2, positional: false, recipe: R(1.5, [
    sine(sw(70, 36, 1.1), 0, 1.35, env(0.04, 0.45, 0.35, 1.2), -3, { drive: 2.2 }),
    noise(Noise.Brown, 0, 1.3, env(0.25, 0.4, 0.2, 1.3), -6, lp(sw(180, 520, 0.8))),
    noise(Noise.Pink, 0.05, 1.15, env(0.3, 0.35, 0, 1.4), -12, bp(sw(2400, 350, 1.0), 2.2)),
    osc(Wave.Saw, sw(55, 46, 1.0), 0.1, 1.1, env(0.2, 0.35, 0.1), -16, { filter: lp(sw(200, 700, 0.9)), vib: { rate: 6, cents: 35 } }),
    thump(120, 42, 1.0, 0.4, 0.14, -6, 1.8),
  ], { delay: { time: 0.16, fb: 0.3, wet: 0.2, damp: 2500 }, drive: 1.2 }) },
  // 수호자(griftGuardian): 깊은 징(98Hz 금속 FM 1:1.414) + 저역 쿵 + 룬이 켜지는 유리 반짝(청금) + 낮은 단3도 합창 패드
  griftGuardian: { cat: C.Grift, prio: 7, gainDb: -2, positional: true, recipe: R(1.45, [
    thump(110, 40, 0, 0.5, 0.16, -3, 2.2),
    sine(98, 0, 1.4, env(0.002, 0.5), -5, { fm: bell(1.414, 3.5, 0.35) }),
    ...[146.83, 174.61, 220].map((f) => osc(Wave.Saw, f, 0.08, 1.25, env(0.18, 0.4, 0.1), -20, { filter: lp(900), vib: { rate: 4.5, cents: 10 } })),
    grains(14, 2200, 6500, 0.12, 0.7, 0.09, env(0.001, 0.03), -18, { order: 1, fm: bell(3.5, 1.2, 0.03) }),
  ], { delay: { time: 0.13, fb: 0.35, wet: 0.22, damp: 4500 } }) },
  // 돌파(griftClear): 저역 붐 + 솟는 쉬익(400→6k) + 상승 화음 아르페지오 D5 F#5 A5 D6 → 지속 화음(벨 FM) + 반짝이 꼬리. 단계가 오를수록 반음씩 위(griftSfx)
  griftClear: { cat: C.Grift, prio: 8, gainDb: 0, positional: false, recipe: R(1.5, [
    thump(100, 40, 0, 0.5, 0.16, -4, 2.2),
    noise(Noise.Pink, 0, 0.8, env(0.45, 0.2, 0, 1.5), -10, bp(sw(400, 6000, 0.7), 2)),
    ...notes([587.33, 739.99, 880, 1174.66], 0.12, 0.09, 1.15, env(0.004, 0.45), -9, { fm: bell(2, 1.1, 0.14) }),
    ...[293.66, 440, 587.33].map((f) => osc(Wave.Saw, f, 0.4, 1.0, env(0.12, 0.35), -20, { filter: lp(2200), vib: { rate: 5, cents: 7 } })),
    grains(22, 3500, 10000, 0.35, 0.95, 0.11, env(0.001, 0.04), -16, { order: 1, fall: 10, fm: bell(2.76, 1, 0.03) }),
  ], { delay: { time: 0.14, fb: 0.4, wet: 0.28, damp: 7000 }, drive: 1.2 }) },
  // 이정표(griftMilestone, 돌파 화음 위에 겹침): 한 옥타브 위 왕관 벨(D6 A6 D7) + 금속 반짝 비 + 두 번째 저역 붐(기둥 두 번째 충격파 0.35초와 맞춤)
  griftMilestone: { cat: C.Grift, prio: 8, gainDb: -3, positional: false, recipe: R(1.4, [
    thump(90, 36, 0.35, 0.45, 0.15, -5, 2),
    ...notes([1174.66, 1760, 2349.32], 0.3, 0.07, 1.0, env(0.002, 0.4), -10, { fm: bell(3.5, 1.4, 0.1) }),
    grains(30, 4000, 11000, 0.3, 1.0, 0.1, env(0.001, 0.035), -15, { order: 1, fall: 14, fm: bell(1.414, 1.2, 0.02) }),
    noise(Noise.White, 0.3, 0.5, env(0.2, 0.15, 0, 1.4), -18, hp(sw(3000, 9000, 0.4))),
  ], { delay: { time: 0.18, fb: 0.45, wet: 0.3, damp: 8000 } }) },
  // 시간 초과(griftLate): 차분하게 — 부드러운 저역 한 번 + 천천히 내려앉는 두 음(A4 → F4, 느린 시작) + 숨 같은 잡음. 반짝이·상승 없음
  griftLate: { cat: C.Grift, prio: 5, gainDb: -6, positional: false, recipe: R(1.3, [
    sine(sw(80, 55, 0.4), 0, 0.6, env(0.02, 0.22), -8, { drive: 1.3 }),
    sine(440, 0.05, 0.6, env(0.12, 0.25), -10, { fm: bell(2, 0.6, 0.2) }),
    sine(349.23, 0.45, 0.8, env(0.15, 0.3), -10, { fm: bell(2, 0.6, 0.2) }),
    noise(Noise.Pink, 0.1, 1.0, env(0.3, 0.35, 0, 1.3), -20, lp(1200)),
  ], { delay: { time: 0.2, fb: 0.3, wet: 0.2, damp: 3000 } }) },
} satisfies Record<string, Omit<SynthDef, 'name'>>;

export type SynthName = keyof typeof DEFS;
export const SYNTH_NAMES = Object.keys(DEFS) as SynthName[];
/** 이름 → 번호(재생 경로는 번호만 쓴다) */
export const Y = Object.fromEntries(SYNTH_NAMES.map((n, i) => [n, i])) as { readonly [K in SynthName]: number };
export const SYNTH_DEFS: readonly SynthDef[] = SYNTH_NAMES.map((name) => ({ name, ...DEFS[name] }));

/** 레시피 변형 수(시드별로 미리 렌더). 재생마다 시드 난수로 변형을 고르고(같은 변형 연속 회피) 피치·음량을 조금씩 흔든다 */
export const VARIANTS = 2;
/** 렌더·버퍼 샘플레이트(Hz). 16kHz 위는 효과음 체감에 거의 기여하지 않고, 은행 메모리가 48kHz 대비 2/3 (합계 약 5.8MB, c022 Lyra 활·화살 10종 +1.3MB ≈ 7.1MB) */
export const SYNTH_SR = 32000;
/** 변형 시드: 레시피 번호·변형 번호로 고정 */
export function variantSeed(id: number, v: number): number {
  return (0x51ed270b ^ Math.imul(id + 1, 0x9e3779b1) ^ Math.imul(v + 1, 0x85ebca6b)) >>> 0;
}
